export type AppRole = 'USER' | 'ADMIN' | 'COACH' | 'REFEREE' | 'MANAGER';

export type ViewAsRole = 'user' | 'admin' | 'coach' | 'referee' | 'manager';

const ROLE_PRIORITY: Record<AppRole, number> = {
  ADMIN: 50,
  MANAGER: 40,
  COACH: 30,
  REFEREE: 20,
  USER: 10,
};

const LEGACY_VIEW_AS_TO_ROLE: Record<string, AppRole> = {
  admin: 'ADMIN',
  manager: 'MANAGER',
  coach: 'COACH',
  referee: 'REFEREE',
  user: 'USER',
  tournament_manager: 'MANAGER',
  team_manager: 'COACH',
  participant: 'USER',
};

export const normalizeAppRole = (role?: string): AppRole | undefined => {
  if (!role) {
    return undefined;
  }

  const normalized = role.toUpperCase();

  if (normalized === 'ADMIN') {
    return 'ADMIN';
  }

  if (
    normalized === 'MANAGER'
    || normalized === 'TOURNAMENT_MANAGER'
    || normalized === 'TOURNAMENTMANAGER'
    || normalized === 'TOURNAMENT_ADMIN'
    || normalized === 'TOURNAMENTADMIN'
  ) {
    return 'MANAGER';
  }

  if (normalized === 'COACH' || normalized === 'TEAM_MANAGER' || normalized === 'TEAMMANAGER') {
    return 'COACH';
  }

  if (normalized === 'REFEREE') {
    return 'REFEREE';
  }

  if (normalized === 'USER' || normalized === 'PARTICIPANT') {
    return 'USER';
  }

  return undefined;
};

export const normalizeAppRoles = (roles?: string[]): AppRole[] => {
  const normalizedRoles = (roles ?? [])
    .map((role) => normalizeAppRole(role))
    .filter((role): role is AppRole => Boolean(role));

  return Array.from(new Set(normalizedRoles));
};

export const getStrongestAppRole = (roles?: string[]): AppRole => {
  const normalizedRoles = normalizeAppRoles(roles);
  if (normalizedRoles.length === 0) {
    return 'USER';
  }

  return normalizedRoles.reduce((strongest, role) => (
    ROLE_PRIORITY[role] > ROLE_PRIORITY[strongest] ? role : strongest
  ), 'USER' as AppRole);
};

export const mapAppRoleToViewAsRole = (role: AppRole): ViewAsRole => {
  if (role === 'ADMIN') return 'admin';
  if (role === 'MANAGER') return 'manager';
  if (role === 'COACH') return 'coach';
  if (role === 'REFEREE') return 'referee';
  return 'user';
};

export const getViewAsRoleFromStorage = (value?: string): ViewAsRole => {
  if (!value) {
    return 'admin';
  }

  if (value in LEGACY_VIEW_AS_TO_ROLE) {
    const role = LEGACY_VIEW_AS_TO_ROLE[value];
    if (role === 'ADMIN') return 'admin';
    if (role === 'MANAGER') return 'manager';
    if (role === 'COACH') return 'coach';
    if (role === 'REFEREE') return 'referee';
    return 'user';
  }

  return 'admin';
};

export const mapViewAsRoleToAppRole = (viewAsRole: string): AppRole => {
  const normalizedView = getViewAsRoleFromStorage(viewAsRole);

  if (normalizedView === 'admin') return 'ADMIN';
  if (normalizedView === 'manager') return 'MANAGER';
  if (normalizedView === 'coach') return 'COACH';
  if (normalizedView === 'referee') return 'REFEREE';
  return 'USER';
};
